import React, { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { createMarkup, useResponsiveType } from "../../utils";

// Interface

export interface HtmlProps {
  children?: any;
  small?: boolean; // For blocks of small print text
  id?: string;
  useCustomBulletPoints?: boolean;
}

interface StyledHtmlProps {
  small?: boolean;
  useCustomBulletPoints?: boolean;
}

// Styles

export const StyledHtml = styled.div<StyledHtmlProps>`
  ${({ theme: { space, colors }, small, useCustomBulletPoints }) => css`
    > * + * {
      margin-block-start: ${space.small};
    }

    ${small &&
    css`
      > * + * {
        margin-block-start: ${space.xSmall};
      }
    `}

    ol,
    ul {
      li + li {
        margin-top: ${space.xSmall};
      }
    }

    p strong {
      ${useResponsiveType("bodyBold")}
    }

    // Type spacing overides

    .secondary + p {
      margin-block-start: ${space.medium};
    }

    p + h3,
    ul + h3,
    ol + h3 {
      margin-block-start: ${space.large};
    }

    h3 + .secondary-small {
      margin-block-start: ${space.xxSmall};
    }

    // Links

    a {
      color: ${colors.darkBlue};
      text-decoration: underline;

      &:hover {
        // TODO ??
      }
    }

    // List styles

    ul {
      list-style-type: none;
      padding-left: 0;

      li {
        position: relative;
        padding-left: calc(${space.xxxSmall} + ${space.xxSmall});

        ${useCustomBulletPoints
          ? css`
              padding-left: ${space.small};
              &:before {
                content: "";
                background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none"><path d="M13.3484 6.67418V5.28001C10.5254 4.91409 8.86971 5.3149 7.49311 5.59373V0H5.87256C5.75059 3.52017 2.94494 6.04699 0 6.32582V7.71999C2.82297 8.08591 4.47865 7.6851 5.85525 7.40627V13H7.47579C7.59776 9.47983 10.4034 6.95301 13.3484 6.67418Z" fill="currentColor"/></svg>');
                background-repeat: no-repeat;
                background-position: 0 50%;
                position: absolute;
                top: ${space.xxxxxSmall};
                border-radius: 50%;
                left: 0;
                width: 16px;
                height: 16px;
              }
            `
          : css`
              padding-left: calc(${space.xxxSmall} + ${space.xxSmall});
              &:before {
                content: "";
                position: absolute;
                top: ${space.xxxSmall};
                border-radius: 50%;
                left: 0;
                width: ${space.xxxSmall};
                height: ${space.xxxSmall};
                background-color: ${colors.darkBlue};
              }
            `}
      }
    }

    ol {
      list-style: none;
      counter-reset: ol-counter;

      li {
        counter-increment: ol-counter;
        position: relative;
        padding-left: calc(${space.small} + ${space.xxSmall});

        &:before {
          content: counter(ol-counter);
          color: ${colors.white};
          background-color: ${colors.darkBlue};
          border-radius: 50%;
          width: ${space.small};
          height: ${space.small};
          display: grid;
          place-items: center;
          ${useResponsiveType("bodyLargeBold")}
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }

    // Typography

    p.large {
      ${useResponsiveType("bodyLarge")}

      strong {
        ${useResponsiveType("bodyLargeBold")}
      }
    }

    p.small {
      ${useResponsiveType("bodySmall")}

      strong {
        ${useResponsiveType("bodySmallBold")}
      }
    }

    h3.secondary {
      ${useResponsiveType("secondaryHeadingMedium")}

      strong {
        ${useResponsiveType("secondaryHeadingMediumBold")}
      }
    }

    h3.secondary-small {
      ${useResponsiveType("secondaryHeadingSmall")}

      strong {
        ${useResponsiveType("secondaryHeadingSmallBold")}
      }
    }

    h1,
    .large {
      ${useResponsiveType("headingLarge")}
    }

    h2 {
      ${useResponsiveType("headingMedium")}
    }

    h3 {
      ${useResponsiveType("headingSmall")}
    }

    h4,
    h5,
    h6 {
      ${useResponsiveType("headingXSmall")}
    }

    strong {
      ${useResponsiveType("bodyBold")}
    }
  `};
`;

// JSX

export const Html: FC<React.PropsWithChildren<HtmlProps>> = ({
  children,
  small,
  id,
  useCustomBulletPoints,
  ...rest
}) => (
  <StyledHtml
    id={id}
    {...rest}
    small={small}
    useCustomBulletPoints={useCustomBulletPoints}
  >
    {typeof children === "string" ? createMarkup(children) : children}
  </StyledHtml>
);

export default Html;
